/**
 * Timeline Track Component (canvas-v2)
 *
 * Single track row displaying video or audio clips.
 * Video mode: N tracks stacked vertically, clips positioned by startOffset.
 * Audio mode: N tracks stacked vertically (60px each) grouped by trackIndex,
 * clips positioned absolutely by startOffset.
 *
 * Memoized for performance with large numbers of clips.
 *
 * RFC-0631 round-3 (Issue 3): the sub-track header column (label + mute +
 * remove) has been hoisted out of this component into TrackHeaderColumn,
 * which lives in a fixed left column outside the horizontal scroll area.
 * This file now only owns the scrolling clip area; headers are siblings
 * rendered by TimelineEditor.
 */

import { memo, useMemo, useRef } from 'react'
import type { TimelineClip, TimelineAudioClip } from './types'
import { TimelineClipComponent } from './TimelineClip'
import { TimelineAudioClipComponent } from './TimelineAudioClip'
import { getClipPosition } from './timelineUtils'
import { createNadDebug } from '@ponpon/api-client'

// RFC-0630 v3 (architect-review #4): audio/video drag-drop observability.
// Fires once per accepted drop; payload includes the resolved clip type,
// target track index, and the drop X offset converted to seconds.
const dndLog = createNadDebug('dw:video-editor:audio-dnd')

interface TimelineTrackProps {
  /** Track type */
  type: 'video' | 'audio'

  /** Track label */
  label: string

  /** Clips on this track */
  clips: TimelineClip[] | TimelineAudioClip[]

  /** Zoom level (pixels per second) */
  zoomLevel: number

  /** Total timeline duration (for track width) */
  totalDuration: number

  /** Set of currently selected clip IDs (multi-selection) */
  selectedClipIds: Set<string>

  /** Callback when clip is selected (replace selection) */
  onSelectClip: (clipId: string) => void

  /** Callback when clip is shift-clicked (toggle membership in multi-selection) */
  onToggleClipSelection?: (clipId: string) => void

  /** Apply uniform startOffset delta + optional track delta to all selected clips */
  onMultiDragOffset?: (deltaSec: number, trackDelta?: number) => void

  /** Callback when clip is reordered (video clips only) */
  onReorderClip?: (clipId: string, newOrder: number) => void

  /** Callback when video startOffset changes */
  onClipOffsetChange?: (clipId: string, startOffset: number) => void

  /** Callback when video track index changes */
  onClipTrackIndexChange?: (clipId: string, newTrackIndex: number) => void

  /** Callback when clip is trimmed */
  onTrimClip: (clipId: string, trimStart: number, trimEnd: number, startOffset?: number) => void

  /** Callback for live trim preview (no history push) */
  onTrimClipPreview?: (clipId: string, trimStart: number, trimEnd: number, startOffset?: number) => void

  /** Callback when audio startOffset changes */
  onAudioOffsetChange?: (clipId: string, startOffset: number) => void

  /** Callback when audio clip is trimmed */
  onAudioTrimClip?: (clipId: string, trimStart: number, trimEnd: number, startOffset?: number) => void

  /** Callback for live audio trim preview (no history push) */
  onAudioTrimClipPreview?: (clipId: string, trimStart: number, trimEnd: number, startOffset?: number) => void

  /** Callback when audio track index changes */
  onAudioTrackIndexChange?: (clipId: string, newTrackIndex: number) => void

  /** Callback when snap position changes during drag */
  onSnapPositionChange?: (position: number | null) => void

  /** Callback fired on mouseUp after trim-start commit (Issue 1) */
  onNormalizeTimelineOrigin?: () => void

  /** Playhead position in pixels (for edge-drag snap, Issue 2) */
  playheadPixel?: number

  /** Track height in pixels (for video track) */
  height?: number

  /** Number of audio tracks (for stacked layout) */
  audioTrackCount?: number

  /** Number of video tracks (for stacked layout) */
  videoTrackCount?: number

  /** Drop a left-panel reference onto this track */
  onDropReference?: (
    clipId: string,
    clipType: 'video' | 'audio',
    startOffset: number,
    trackIndex: number
  ) => void

  /** Whether track is locked */
  locked?: boolean

  /** Minimum width in pixels */
  minWidth?: number
}

const AUDIO_TRACK_HEIGHT = 60

function isTimelineClip(clip: TimelineClip | TimelineAudioClip): clip is TimelineClip {
  return !('role' in clip)
}

function isTimelineAudioClip(clip: TimelineClip | TimelineAudioClip): clip is TimelineAudioClip {
  return 'role' in clip
}

function parseReferencePayload(payload: string): { id: string; type: 'video' | 'audio' } | null {
  const parsed = JSON.parse(payload)
  if (
    parsed &&
    typeof parsed === 'object' &&
    typeof parsed.id === 'string' &&
    (parsed.type === 'video' || parsed.type === 'audio')
  ) {
    return { id: parsed.id, type: parsed.type }
  }
  return null
}

export const TimelineTrack = memo(function TimelineTrack({
  type,
  label,
  clips,
  zoomLevel,
  totalDuration,
  selectedClipIds,
  onSelectClip,
  onToggleClipSelection,
  onMultiDragOffset,
  onReorderClip,
  onClipOffsetChange,
  onClipTrackIndexChange,
  onTrimClip,
  onTrimClipPreview,
  onAudioOffsetChange,
  onAudioTrimClip,
  onAudioTrimClipPreview,
  onAudioTrackIndexChange,
  onSnapPositionChange,
  onNormalizeTimelineOrigin,
  playheadPixel,
  height = 80,
  audioTrackCount = 1,
  videoTrackCount = 1,
  onDropReference,
  locked = false,
  minWidth = 1000,
}: TimelineTrackProps) {
  const trackWidth = Math.max(totalDuration * zoomLevel, minWidth)

  const videoClips = useMemo(
    () => (type === 'video' ? clips.filter(isTimelineClip) : []),
    [type, clips]
  )

  const videoClipsByTrack = useMemo(() => {
    if (type !== 'video') return []
    const groups: TimelineClip[][] = Array.from({ length: videoTrackCount }, () => [])
    videoClips.forEach((clip) => {
      const idx = Math.min(Math.max(0, clip.trackIndex), videoTrackCount - 1)
      groups[idx].push(clip)
    })
    return groups
  }, [type, videoClips, videoTrackCount])

  // Group audio clips by trackIndex
  const audioClipsByTrack = useMemo(() => {
    if (type !== 'audio') return []
    const audioClips = clips.filter(isTimelineAudioClip)
    const groups: TimelineAudioClip[][] = Array.from({ length: audioTrackCount }, () => [])
    audioClips.forEach((clip) => {
      const idx = Math.min(clip.trackIndex, audioTrackCount - 1)
      groups[idx].push(clip)
    })
    return groups
  }, [type, clips, audioTrackCount])

  const trackRowHeight = type === 'audio' ? AUDIO_TRACK_HEIGHT : height
  const trackCount = type === 'audio' ? audioTrackCount : videoTrackCount
  const totalTrackHeight = trackRowHeight * trackCount

  // RFC-0631 (Issue 1): dedupe `dragover-accepted` logs per (type, trackIndex).
  // Reset on dragleave so re-entry is observable.
  const dragOverAcceptedRef = useRef<Set<string>>(new Set())

  const handleDrop = (
    event: React.DragEvent<HTMLDivElement>,
    expectedType: 'video' | 'audio',
    trackIndex: number
  ) => {
    if (!onDropReference || locked) return
    const payload = event.dataTransfer.getData('application/x-ponpon-timeline-reference')
    if (!payload) return
    event.preventDefault()
    event.stopPropagation()
    try {
      const parsed = parseReferencePayload(payload)
      if (!parsed || parsed.type !== expectedType) return
      const rect = event.currentTarget.getBoundingClientRect()
      const startOffset = Math.max(0, (event.clientX - rect.left) / zoomLevel)
      dndLog({
        event: 'reference-dropped',
        clipType: parsed.type,
        referenceId: parsed.id,
        trackIndex,
        startOffsetS: Number(startOffset.toFixed(3)),
      })
      onDropReference(parsed.id, expectedType, startOffset, trackIndex)
    } catch {
      // Ignore malformed drag payloads from outside the editor.
    }
  }

  const handleDragOver = (
    event: React.DragEvent<HTMLDivElement>,
    expectedType: 'video' | 'audio',
    trackIndex: number
  ) => {
    if (locked) return
    // RFC-0631 (Issue 1): HTML5 `dragover`/`dragenter` cannot read `getData()` —
    // browsers expose only `types` for security. Reading `getData()` returns ''
    // so the old gate never called preventDefault, leaving dropEffect='none'
    // and the drop event never firing. Gate on `types.includes(...)` instead.
    if (!event.dataTransfer.types.includes('application/x-ponpon-timeline-reference')) return
    event.preventDefault()
    event.dataTransfer.dropEffect = 'copy'
    const key = `${expectedType}:${trackIndex}`
    if (!dragOverAcceptedRef.current.has(key)) {
      dragOverAcceptedRef.current.add(key)
      dndLog({
        event: 'dragover-accepted',
        expectedType,
        trackIndex,
      })
    }
  }

  const handleDragLeave = (
    event: React.DragEvent<HTMLDivElement>,
    expectedType: 'video' | 'audio',
    trackIndex: number
  ) => {
    // Reset the per-(type, trackIndex) dedupe key so re-entry logs again.
    void event
    dragOverAcceptedRef.current.delete(`${expectedType}:${trackIndex}`)
  }

  return (
    <div className="border-b border-border" data-testid={`timeline-track-body-${label.toLowerCase()}`}>
      {/* Track content area */}
      <div
        className="relative bg-canvas"
        style={{
          width: `${trackWidth}px`,
          height: `${totalTrackHeight}px`,
        }}
      >
        {/* RFC-0631 round-3 (Issue 3): the sub-track label and the mute
            button have moved to TrackHeaderColumn (a fixed-left sibling).
            The in-track "Video"/"Audio" badge that used to sit inside the
            scrollable area has been retired as well — its information is
            duplicated in the column. */}

        {/* Video clips -- stacked vertically by trackIndex */}
        {type === 'video' && onReorderClip && videoClipsByTrack.map((trackClips, trackIdx) => {
          return (
          <div
            key={trackIdx}
            className="absolute left-0 right-0"
            style={{
              top: `${trackIdx * height}px`,
              height: `${height}px`,
            }}
            onDrop={(event) => handleDrop(event, 'video', trackIdx)}
            onDragOver={(event) => handleDragOver(event, 'video', trackIdx)}
            onDragLeave={(event) => handleDragLeave(event, 'video', trackIdx)}
            data-testid={`timeline-video-track-${trackIdx}`}
          >
            {trackIdx > 0 && (
              <div className="absolute top-0 left-0 right-0 h-px bg-border/50" />
            )}
            <div className="relative h-full">
              {trackClips.map((clip) => {
                const position = getClipPosition(clip, videoClips, zoomLevel)

                return (
                  <div
                    key={clip.id}
                    className="absolute top-2 bottom-2"
                    style={{ left: `${position}px` }}
                  >
                    <TimelineClipComponent
                      clip={clip}
                      allClips={videoClips}
                      zoomLevel={zoomLevel}
                      isSelected={selectedClipIds.has(clip.id)}
                      isMultiSelectionMember={selectedClipIds.size > 1 && selectedClipIds.has(clip.id)}
                      trackHeight={height - 16}
                      onSelect={onSelectClip}
                      onToggleSelect={onToggleClipSelection}
                      onMultiDragOffset={onMultiDragOffset}
                      onReorder={onReorderClip}
                      onOffsetChange={onClipOffsetChange}
                      onTrackIndexChange={onClipTrackIndexChange}
                      onTrim={onTrimClip}
                      onTrimPreview={onTrimClipPreview}
                      onSnapPositionChange={onSnapPositionChange}
                      onNormalizeTimelineOrigin={onNormalizeTimelineOrigin}
                      playheadPixel={playheadPixel}
                      locked={locked}
                      videoTrackCount={videoTrackCount}
                    />
                  </div>
                )
              })}
            </div>
          </div>
          )
        })}

        {/* Audio clips -- stacked vertically by trackIndex */}
        {type === 'audio' && audioClipsByTrack.map((trackClips, trackIdx) => {
          return (
          <div
            key={trackIdx}
            className="absolute left-0 right-0"
            style={{
              top: `${trackIdx * AUDIO_TRACK_HEIGHT}px`,
              height: `${AUDIO_TRACK_HEIGHT}px`,
            }}
            onDrop={(event) => handleDrop(event, 'audio', trackIdx)}
            onDragOver={(event) => handleDragOver(event, 'audio', trackIdx)}
            onDragLeave={(event) => handleDragLeave(event, 'audio', trackIdx)}
            data-testid={`timeline-audio-track-${trackIdx}`}
          >
            {/* Track separator line (except first) */}
            {trackIdx > 0 && (
              <div className="absolute top-0 left-0 right-0 h-px bg-border/50" />
            )}

            {/* Audio clips on this sub-track */}
            <div className="relative h-full">
              {trackClips.map((clip) => (
                <div
                  key={clip.id}
                  className="absolute top-2 bottom-2"
                  style={{ left: '0px' }}
                >
                  <TimelineAudioClipComponent
                    clip={clip}
                    zoomLevel={zoomLevel}
                    isSelected={selectedClipIds.has(clip.id)}
                    isMultiSelectionMember={selectedClipIds.size > 1 && selectedClipIds.has(clip.id)}
                    onSelect={onSelectClip}
                    onToggleSelect={onToggleClipSelection}
                    onMultiDragOffset={onMultiDragOffset}
                    onTrim={onAudioTrimClip || onTrimClip}
                    onTrimPreview={onAudioTrimClipPreview}
                    onOffsetChange={onAudioOffsetChange || (() => {})}
                    onTrackIndexChange={onAudioTrackIndexChange}
                    audioTrackCount={audioTrackCount}
                    trackHeight={AUDIO_TRACK_HEIGHT}
                    locked={locked}
                  />
                </div>
              ))}
            </div>
          </div>
          )
        })}

        {/* Empty state */}
        {clips.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-foreground/30 text-sm">
              {type === 'video' ? 'No video clips' : 'No audio clips'}
            </div>
          </div>
        )}
      </div>
    </div>
  )
})

/* TrackHeader was removed in RFC-0631 round-3 (Issue 3) — it has been
 * hoisted into TrackHeaderColumn so it can live outside the horizontal
 * scroll area. */

